import React, { useEffect, useState } from "react"
import {
  XMarkIcon,
  ArchiveBoxIcon,
  ServerIcon,
} from "@heroicons/react/24/solid"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Dropdown from "aldoo-ra/Dropdown"
import Typography from "aldoo-ra/Typography"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import moment from "moment"

const RestoreDialog = ({ selectedBackup, onClose, onRestore }) => {
  const sections = [
    {
      id: "database",
      title: "Database",
      icon: ServerIcon,
      options: [
        { key: "new-docs-only", value: "New Documents Only" },
        { key: "merge", value: "Merge" },
        { key: "upsert", value: "Update" },
        { key: "drop", value: "Backup Mirror" },
      ],
      helpText: {
        merge:
          "Updates existing fields to match the Backup. New fields from the Backup will be added to the Source. Source documents that are not in the Backup are left unchanged.",
        upsert:
          "Updates existing fields to match the Backup. Fields not present in the Backup will be removed. Source documents that are not in the Backup are left unchanged.",
        drop: "Replace the Source with the Backup. WARNING: All existing Source documents will be deleted prior to import.",
        "new-docs-only":
          "Only import documents that are not present in the Source. Existing documents are left unchanged.",
      },
    },
    {
      id: "static-files",
      title: "Static Files",
      icon: ArchiveBoxIcon,
      options: [],
    },
    {
      id: "deployment",
      title: "Deployment",
      icon: ArchiveBoxIcon,
      options: [],
    },
  ]

  const [activeSection, setActiveSection] = useState(sections[0].id)
  const [selectedMode, setSelectedMode] = useState(sections[0].options[2])
  const [enabledSections, setEnabledSections] = useState({
    database: true,
    "static-files": true,
    deployment: false,
  })
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    setIsOpen(!!selectedBackup)
  }, [selectedBackup])

  const handleStartRestore = () => {
    const restoreOptions = {
      DB_RESTORE_MODE: selectedMode.key,
      INCLUDE_STATIC_FILES: enabledSections["static-files"],
      INCLUDE_DATABASE: enabledSections.database,
      INCLUDE_DEPLOYMENT: enabledSections.deployment,
    }

    onRestore(selectedBackup, restoreOptions)
    onClose()
  }

  const toggleSection = (sectionId) => {
    setEnabledSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }))
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setIsOpen(false)
      }}
      variant="admin"
      className="p-6 sm:w-[600px] h-full md:h-auto"
      title="Restore Options"
    >
      <div className="mb-8 flex flex-col items-center justify-center">
        <div className="w-16 h-16 rounded-full border-2 border-admin_bordered flex items-center justify-center mb-4">
          <ServerIcon className="w-8 h-8 text-primary" />
        </div>
        <div className="text-lg text-center text-primary">
          {selectedBackup?.path.split("/").pop()}
        </div>
        <div className="flex flex-col gap-1 items-center mt-5">
          <span className="text-primary">
            {moment(selectedBackup.timeCreated).format("YYYY-MM-DD | HH:mm")}
          </span>
          <span className="text-admin_neutral_2 text-sm">
            {moment(selectedBackup.timeCreated).fromNow()}
          </span>
        </div>
      </div>

      <div className="flex border-b mb-6 min-h-[40px]">
        {sections.map((section) => (
          <div
            key={section.id}
            className={`flex items-center mr-8 pb-2 cursor-pointer ${
              activeSection === section.id ? "border-b-2 border-admin_primary/50" : ""
            }`}
            onClick={() => setActiveSection(section.id)}
          >
            <span
              className={
                activeSection === section.id ? "text-primary" : "text-admin_text_secondary"
              }
            >
              {section.title}
            </span>
          </div>
        ))}
      </div>

      <div className="min-h-[200px] mb-4">
        <div className="flex items-center mb-4">
          <span className="text-admin_text_secondary mr-2">
            {`Restore ${sections.find((s) => s.id === activeSection).title}`}
          </span>
          <ToggleSwitch
            variant="admin"
            isOn={enabledSections[activeSection]}
            onChange={() => toggleSection(activeSection)}
          />
        </div>

        {activeSection === "database" && (
          <div>
            <Typography variant="admin_body" className="!text-admin_text_secondary">
              Import Mode
            </Typography>
            <Dropdown
              options={sections[0].options}
              value={selectedMode}
              className="w-full"
              onItemRender={(option) => option.value}
              onChange={(option) => setSelectedMode(option)}
              sortBy="key"
              menuPosition="bottom"
            />
            <Typography variant="admin_body" className="mt-2 !text-admin_neutral_2">
              {sections[0].helpText[selectedMode.key]}
            </Typography>
          </div>
        )}
      </div>

      <div className="mx-auto w-48 mt-10 space-x-4">
        <Button
          variant="admin_secondary"
          onClick={handleStartRestore}
          className="w-full bg-admin_primary text-admin_text_inv hover:bg-admin_primary_dk"
        >
          Restore
        </Button>
      </div>
    </Modal>
  )
}

export default RestoreDialog
